/**
 * DocumentTemplatesSection - Refactored Version
 * 
 * Manages document templates, custom merge fields, and field groups.
 * Uses extracted sub-components for better maintainability.
 */
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { toast } from 'sonner';
import {
  FileText, Plus, Upload, Download, Trash2, Edit, FileCode, Loader2,
  Copy, CheckCircle, Code2, File, Settings, Wand2, FolderOpen, List,
  Link2, GitBranch, Info
} from 'lucide-react';
import AITemplateConverter from './AITemplateConverter';

// Import extracted components
import { 
  API_URL, categoryColors, groupColors, fieldTypeLabels, calculationOperators,
  getGroupIcon, availableIcons, availableColors, generateTag, formatFileSize,
  initialCustomFieldData, initialGroupData
} from './templates/templateConstants';
import DocumentTemplatesTab from './templates/DocumentTemplatesTab';
import FieldGroupsTab from './templates/FieldGroupsTab';
import MergeFieldsHelpDialog from './templates/MergeFieldsHelpDialog';
import CustomFieldRow from './templates/CustomFieldRow';

export default function DocumentTemplatesSection() {
  const { token } = useAuth();

  // Core data state
  const [templates, setTemplates] = useState([]);
  const [mergeFieldsData, setMergeFieldsData] = useState({ default_fields: [], custom_fields: [] });
  const [customFields, setCustomFields] = useState([]);
  const [fieldGroups, setFieldGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('templates');
  const [calculationTemplates, setCalculationTemplates] = useState({});

  // Merge fields dialog
  const [showMergeFieldsDialog, setShowMergeFieldsDialog] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Custom field dialog state
  const [showCustomFieldDialog, setShowCustomFieldDialog] = useState(false);
  const [editingCustomField, setEditingCustomField] = useState(null);
  const [customFieldData, setCustomFieldData] = useState(initialCustomFieldData);
  const [savingCustomField, setSavingCustomField] = useState(false);
  const [showValidationOptions, setShowValidationOptions] = useState(false);
  const [showDependencyOptions, setShowDependencyOptions] = useState(false);
  const [showCalculationOptions, setShowCalculationOptions] = useState(false);
  const [showStateOverrides, setShowStateOverrides] = useState(false);

  // Group fields by group_id
  const { groupedFields, ungroupedFields } = useMemo(() => {
    const grouped = {};
    const ungrouped = [];
    
    customFields.forEach(field => {
      if (field.group_id) {
        if (!grouped[field.group_id]) grouped[field.group_id] = [];
        grouped[field.group_id].push(field);
      } else {
        ungrouped.push(field);
      }
    });
    
    return { groupedFields: grouped, ungroupedFields: ungrouped };
  }, [customFields]);

  // Fetch functions
  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/document-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchMergeFields = async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/document-templates/merge-fields`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMergeFieldsData(data);
      }
    } catch (error) {
      console.error('Error fetching merge fields:', error);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/custom-merge-fields`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCustomFields(data.fields || []);
      }
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFieldGroups = async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/field-groups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFieldGroups(data.groups || []);
      }
    } catch (error) {
      console.error('Error fetching field groups:', error);
    }
  };

  const fetchCalculationTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/calculation-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCalculationTemplates(data.templates || {});
      }
    } catch (error) {
      console.error('Error fetching calculation templates:', error);
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchMergeFields();
    fetchCustomFields();
    fetchFieldGroups();
    fetchCalculationTemplates();
  }, [token]);

  // Refresh all data
  const refreshAll = () => {
    fetchTemplates();
    fetchMergeFields();
    fetchCustomFields();
    fetchFieldGroups();
  };

  // Copy merge field to clipboard
  const copyMergeField = (tag) => {
    navigator.clipboard.writeText(tag);
    setCopiedField(tag);
    setTimeout(() => setCopiedField(null), 2000);
    toast.success('Copied to clipboard');
  };

  // Custom field handlers
  const handleSaveCustomField = async () => {
    if (!customFieldData.name || !customFieldData.tag) {
      toast.error('Name and tag are required');
      return;
    }

    setSavingCustomField(true);
    try {
      // Build validation object
      const validation = {};
      const v = customFieldData.validation;
      if (v.min_length) validation.min_length = parseInt(v.min_length);
      if (v.max_length) validation.max_length = parseInt(v.max_length);
      if (v.pattern) validation.pattern = v.pattern;
      if (v.pattern_description) validation.pattern_description = v.pattern_description;
      if (v.min_value) validation.min_value = parseFloat(v.min_value);
      if (v.max_value) validation.max_value = parseFloat(v.max_value);
      if (v.required_for_case_types) {
        validation.required_for_case_types = v.required_for_case_types.split(',').map(t => t.trim()).filter(Boolean);
      }
      if (v.custom_error_message) validation.custom_error_message = v.custom_error_message;

      // Build dependency object
      const dependency = {};
      const d = customFieldData.dependency;
      if (d.depends_on) {
        dependency.depends_on = d.depends_on;
        dependency.condition = d.condition || 'exists';
        dependency.value = d.value || '';
        dependency.make_required = d.make_required || false;
      }

      // Build calculation object for calculation fields
      let calculation = null;
      if (customFieldData.field_type === 'calculation') {
        calculation = {
          operator: customFieldData.calculation.operator,
          source_fields: customFieldData.calculation.source_fields || [],
          custom_formula: customFieldData.calculation.custom_formula || '',
          round_to: customFieldData.calculation.round_to ?? 2,
          format_as_currency: customFieldData.calculation.format_as_currency ?? true,
          state_overrides: customFieldData.calculation.state_overrides || [],
          case_type_formulas: customFieldData.calculation.case_type_formulas || {},
          description: customFieldData.calculation.description || ''
        };
      }

      const payload = {
        name: customFieldData.name,
        tag: customFieldData.tag,
        description: customFieldData.description || '',
        field_type: customFieldData.field_type,
        placeholder: customFieldData.placeholder || '',
        default_value: customFieldData.default_value || '',
        options: customFieldData.options ? customFieldData.options.split(',').map(o => o.trim()) : [],
        required: customFieldData.required,
        category: customFieldData.category || 'custom',
        group_id: customFieldData.group_id || null,
        validation: Object.keys(validation).length > 0 ? validation : null,
        dependency: Object.keys(dependency).length > 0 ? dependency : null,
        calculation
      };

      const url = editingCustomField
        ? `${API_URL}/api/case-management/custom-merge-fields/${editingCustomField.id}`
        : `${API_URL}/api/case-management/custom-merge-fields`;

      const res = await fetch(url, {
        method: editingCustomField ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success(editingCustomField ? 'Field updated' : 'Field created');
        setShowCustomFieldDialog(false);
        setEditingCustomField(null);
        setCustomFieldData(initialCustomFieldData);
        setShowValidationOptions(false);
        setShowDependencyOptions(false);
        setShowCalculationOptions(false);
        setShowStateOverrides(false);
        fetchCustomFields();
        fetchMergeFields();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save field');
      }
    } catch (error) {
      toast.error('Failed to save field');
    } finally {
      setSavingCustomField(false);
    }
  };

  const handleDeleteCustomField = async (fieldId) => {
    if (!confirm('Are you sure you want to delete this custom field?')) return;

    try {
      const res = await fetch(`${API_URL}/api/case-management/custom-merge-fields/${fieldId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Field deleted');
        fetchCustomFields();
        fetchMergeFields();
      } else {
        toast.error('Failed to delete field');
      }
    } catch (error) {
      toast.error('Failed to delete field');
    }
  };

  const openEditCustomField = (field) => {
    setEditingCustomField(field);
    const v = field.validation || {};
    const d = field.dependency || {};
    const c = field.calculation || {};
    
    setCustomFieldData({
      name: field.name,
      tag: field.tag,
      description: field.description || '',
      field_type: field.field_type || 'text',
      placeholder: field.placeholder || '',
      default_value: field.default_value || '',
      options: field.options?.join(', ') || '',
      required: field.required || false,
      category: field.category || 'custom',
      group_id: field.group_id || '',
      validation: {
        min_length: v.min_length?.toString() || '',
        max_length: v.max_length?.toString() || '',
        pattern: v.pattern || '',
        pattern_description: v.pattern_description || '',
        min_value: v.min_value?.toString() || '',
        max_value: v.max_value?.toString() || '',
        required_for_case_types: v.required_for_case_types?.join(', ') || '',
        custom_error_message: v.custom_error_message || ''
      },
      dependency: {
        depends_on: d.depends_on || '',
        condition: d.condition || 'exists',
        value: d.value || '',
        make_required: d.make_required || false
      },
      calculation: {
        operator: c.operator || 'sum',
        source_fields: c.source_fields || [],
        custom_formula: c.custom_formula || '',
        round_to: c.round_to ?? 2,
        format_as_currency: c.format_as_currency ?? true,
        state_overrides: c.state_overrides || [],
        case_type_formulas: c.case_type_formulas || {},
        description: c.description || ''
      }
    });
    setShowValidationOptions(Object.keys(v).length > 0);
    setShowDependencyOptions(!!d.depends_on);
    setShowCalculationOptions(field.field_type === 'calculation');
    setShowStateOverrides((c.state_overrides || []).length > 0);
    setShowCustomFieldDialog(true);
  };

  const openNewCustomField = () => {
    setEditingCustomField(null);
    setCustomFieldData(initialCustomFieldData);
    setShowValidationOptions(false);
    setShowDependencyOptions(false);
    setShowCalculationOptions(false);
    setShowStateOverrides(false);
    setShowCustomFieldDialog(true);
  };

  // Get numeric/currency fields for calculation source selection
  const numericFields = customFields.filter(f => 
    ['number', 'currency', 'calculation'].includes(f.field_type) && 
    f.tag !== customFieldData.tag
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span>Loading templates...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="templates" data-testid="templates-tab">
              <FileText className="w-4 h-4 mr-2" />
              Templates
            </TabsTrigger>
            <TabsTrigger value="custom-fields" data-testid="custom-fields-tab">
              <Settings className="w-4 h-4 mr-2" />
              Custom Fields
            </TabsTrigger>
            <TabsTrigger value="field-groups" data-testid="field-groups-tab">
              <FolderOpen className="w-4 h-4 mr-2" />
              Field Groups
            </TabsTrigger>
          </TabsList>
          <Button variant="outline" onClick={() => setShowMergeFieldsDialog(true)}>
            <Code2 className="w-4 h-4 mr-2" />
            View Merge Fields
          </Button>
        </div>

        {/* Document Templates Tab */}
        <TabsContent value="templates" className="mt-6">
          <DocumentTemplatesTab
            token={token}
            templates={templates}
            onRefresh={refreshAll}
          />
        </TabsContent>

        {/* Custom Fields Tab */}
        <TabsContent value="custom-fields" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Custom Merge Fields</CardTitle>
                  <CardDescription>
                    Create custom fields that can be filled in on each case and used in document templates
                  </CardDescription>
                </div>
                <Button onClick={openNewCustomField} data-testid="add-custom-field-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Field
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {customFields.length > 0 ? (
                <div className="space-y-6">
                  {/* Grouped fields */}
                  {fieldGroups.map(group => {
                    const fieldsInGroup = groupedFields[group.id] || [];
                    if (fieldsInGroup.length === 0) return null;

                    const IconComponent = getGroupIcon(group.icon);
                    return (
                      <div key={group.id} className="space-y-2">
                        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${groupColors[group.color] || groupColors.slate}`}>
                          <IconComponent className="w-4 h-4" />
                          <span className="font-medium text-sm">{group.name}</span>
                          <Badge variant="secondary" className="text-xs ml-auto">{fieldsInGroup.length} fields</Badge>
                        </div>
                        <div className="pl-4 space-y-2">
                          {fieldsInGroup.map(field => (
                            <CustomFieldRow
                              key={field.id}
                              field={field}
                              customFields={customFields}
                              onEdit={() => openEditCustomField(field)}
                              onDelete={() => handleDeleteCustomField(field.id)}
                              onCopy={copyMergeField}
                              copiedField={copiedField}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}

                  {/* Ungrouped fields */}
                  {ungroupedFields.length > 0 && (
                    <div className="space-y-2">
                      {fieldGroups.length > 0 && (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-slate-50 border-slate-200">
                          <List className="w-4 h-4 text-slate-600" />
                          <span className="font-medium text-sm text-slate-700">Ungrouped Fields</span>
                          <Badge variant="secondary" className="text-xs ml-auto">{ungroupedFields.length} fields</Badge>
                        </div>
                      )}
                      <div className={fieldGroups.length > 0 ? 'pl-4 space-y-2' : 'space-y-2'}>
                        {ungroupedFields.map(field => (
                          <CustomFieldRow
                            key={field.id}
                            field={field}
                            customFields={customFields}
                            onEdit={() => openEditCustomField(field)}
                            onDelete={() => handleDeleteCustomField(field.id)}
                            onCopy={copyMergeField}
                            copiedField={copiedField}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Settings className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Custom Merge Fields</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Create custom fields to capture case-specific data like policy numbers, claim IDs, etc.
                  </p>
                  <Button onClick={openNewCustomField}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Field
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Field Groups Tab */}
        <TabsContent value="field-groups" className="mt-6">
          <FieldGroupsTab
            token={token}
            fieldGroups={fieldGroups}
            customFields={customFields}
            onRefresh={fetchFieldGroups}
            onRefreshFields={fetchCustomFields}
          />
        </TabsContent>
      </Tabs>

      {/* Merge Fields Help Dialog */}
      <MergeFieldsHelpDialog
        open={showMergeFieldsDialog}
        onOpenChange={setShowMergeFieldsDialog}
        mergeFieldsData={mergeFieldsData}
        customFields={customFields}
      />

      {/* Custom Field Dialog */}
      <Dialog open={showCustomFieldDialog} onOpenChange={setShowCustomFieldDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-indigo-600" />
              {editingCustomField ? 'Edit Custom Field' : 'Add Custom Field'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="space-y-2">
              <Label>Field Name *</Label>
              <Input
                placeholder="e.g., Policy Number"
                value={customFieldData.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setCustomFieldData({
                    ...customFieldData,
                    name,
                    tag: customFieldData.tag || generateTag(name)
                  });
                }}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Merge Tag *</Label>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">{'{{'}</span>
                <Input
                  placeholder="policy_number"
                  value={customFieldData.tag}
                  onChange={(e) => setCustomFieldData({
                    ...customFieldData,
                    tag: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')
                  })}
                  className="font-mono"
                />
                <span className="text-muted-foreground">{'}}'}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Field Group</Label>
              <Select
                value={customFieldData.group_id || "none"}
                onValueChange={(v) => setCustomFieldData({ ...customFieldData, group_id: v === "none" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="No group (ungrouped)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No group</SelectItem>
                  {fieldGroups.map(group => (
                    <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="What this field is for..."
                value={customFieldData.description}
                onChange={(e) => setCustomFieldData({ ...customFieldData, description: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Field Type</Label>
              <Select
                value={customFieldData.field_type}
                onValueChange={(v) => {
                  setCustomFieldData({ ...customFieldData, field_type: v });
                  setShowCalculationOptions(v === 'calculation');
                }}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(fieldTypeLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Calculation Options */}
            {customFieldData.field_type === 'calculation' && (
              <div className="p-4 border rounded-lg bg-purple-50 space-y-4">
                <h4 className="font-medium text-sm text-purple-800">Calculation Settings</h4>
                
                <div className="space-y-2">
                  <Label>Operator</Label>
                  <Select
                    value={customFieldData.calculation.operator}
                    onValueChange={(v) => setCustomFieldData({
                      ...customFieldData,
                      calculation: { ...customFieldData.calculation, operator: v }
                    })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(calculationOperators).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Source Fields</Label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {numericFields.map(field => (
                      <div key={field.tag} className="flex items-center gap-2">
                        <Checkbox
                          checked={customFieldData.calculation.source_fields?.includes(field.tag)}
                          onCheckedChange={(checked) => {
                            const current = customFieldData.calculation.source_fields || [];
                            setCustomFieldData({
                              ...customFieldData,
                              calculation: {
                                ...customFieldData.calculation,
                                source_fields: checked 
                                  ? [...current, field.tag]
                                  : current.filter(t => t !== field.tag)
                              }
                            });
                          }}
                        />
                        <span className="text-sm">{field.name}</span>
                        <code className="text-xs bg-slate-200 px-1 rounded">{field.tag}</code>
                      </div>
                    ))}
                    {numericFields.length === 0 && (
                      <p className="text-xs text-muted-foreground">No numeric/currency fields available</p>
                    )}
                  </div>
                </div>

                {customFieldData.calculation.operator === 'custom' && (
                  <div className="space-y-2">
                    <Label>Custom Formula</Label>
                    <Input
                      placeholder="field1 + field2 * 0.5"
                      value={customFieldData.calculation.custom_formula}
                      onChange={(e) => setCustomFieldData({
                        ...customFieldData,
                        calculation: { ...customFieldData.calculation, custom_formula: e.target.value }
                      })}
                      className="font-mono"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Round to decimals</Label>
                    <Input
                      type="number"
                      min="0"
                      max="6"
                      value={customFieldData.calculation.round_to}
                      onChange={(e) => setCustomFieldData({
                        ...customFieldData,
                        calculation: { ...customFieldData.calculation, round_to: parseInt(e.target.value) || 2 }
                      })}
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <Switch
                      checked={customFieldData.calculation.format_as_currency}
                      onCheckedChange={(checked) => setCustomFieldData({
                        ...customFieldData,
                        calculation: { ...customFieldData.calculation, format_as_currency: checked }
                      })}
                    />
                    <Label>Format as currency</Label>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description (shown to users)</Label>
                  <Input
                    placeholder="How this calculation works..."
                    value={customFieldData.calculation.description}
                    onChange={(e) => setCustomFieldData({
                      ...customFieldData,
                      calculation: { ...customFieldData.calculation, description: e.target.value }
                    })}
                  />
                </div>
              </div>
            )}

            {/* Dropdown Options */}
            {customFieldData.field_type === 'select' && (
              <div className="space-y-2">
                <Label>Options (comma-separated)</Label>
                <Input
                  placeholder="Option 1, Option 2, Option 3"
                  value={customFieldData.options}
                  onChange={(e) => setCustomFieldData({ ...customFieldData, options: e.target.value })}
                />
              </div>
            )}

            {/* Placeholder & Default */}
            {!['checkbox', 'calculation'].includes(customFieldData.field_type) && (
              <>
                <div className="space-y-2">
                  <Label>Placeholder</Label>
                  <Input
                    placeholder="Placeholder text..."
                    value={customFieldData.placeholder}
                    onChange={(e) => setCustomFieldData({ ...customFieldData, placeholder: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Default Value</Label>
                  <Input
                    placeholder="Default value..."
                    value={customFieldData.default_value}
                    onChange={(e) => setCustomFieldData({ ...customFieldData, default_value: e.target.value })}
                  />
                </div>
              </>
            )}

            {/* Required toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <Label>Required Field</Label>
              <Switch
                checked={customFieldData.required}
                onCheckedChange={(checked) => setCustomFieldData({ ...customFieldData, required: checked })}
              />
            </div>

            {/* Validation Options Toggle */}
            <div className="border-t pt-4">
              <Button
                variant="ghost"
                className="w-full justify-between"
                onClick={() => setShowValidationOptions(!showValidationOptions)}
              >
                <span className="flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Validation Rules
                </span>
                <span>{showValidationOptions ? '−' : '+'}</span>
              </Button>
              
              {showValidationOptions && (
                <div className="mt-4 p-4 border rounded-lg space-y-4">
                  {['text', 'textarea'].includes(customFieldData.field_type) && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Min Length</Label>
                        <Input
                          type="number"
                          value={customFieldData.validation.min_length}
                          onChange={(e) => setCustomFieldData({
                            ...customFieldData,
                            validation: { ...customFieldData.validation, min_length: e.target.value }
                          })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Max Length</Label>
                        <Input
                          type="number"
                          value={customFieldData.validation.max_length}
                          onChange={(e) => setCustomFieldData({
                            ...customFieldData,
                            validation: { ...customFieldData.validation, max_length: e.target.value }
                          })}
                        />
                      </div>
                    </div>
                  )}
                  
                  {['number', 'currency'].includes(customFieldData.field_type) && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Min Value</Label>
                        <Input
                          type="number"
                          value={customFieldData.validation.min_value}
                          onChange={(e) => setCustomFieldData({
                            ...customFieldData,
                            validation: { ...customFieldData.validation, min_value: e.target.value }
                          })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Max Value</Label>
                        <Input
                          type="number"
                          value={customFieldData.validation.max_value}
                          onChange={(e) => setCustomFieldData({
                            ...customFieldData,
                            validation: { ...customFieldData.validation, max_value: e.target.value }
                          })}
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Required for Case Types (comma-separated)</Label>
                    <Input
                      placeholder="personal_injury, auto_accident"
                      value={customFieldData.validation.required_for_case_types}
                      onChange={(e) => setCustomFieldData({
                        ...customFieldData,
                        validation: { ...customFieldData.validation, required_for_case_types: e.target.value }
                      })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Custom Error Message</Label>
                    <Input
                      placeholder="Please enter a valid..."
                      value={customFieldData.validation.custom_error_message}
                      onChange={(e) => setCustomFieldData({
                        ...customFieldData,
                        validation: { ...customFieldData.validation, custom_error_message: e.target.value }
                      })}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Dependency Options Toggle */}
            <div className="border-t pt-4">
              <Button
                variant="ghost"
                className="w-full justify-between"
                onClick={() => setShowDependencyOptions(!showDependencyOptions)}
              >
                <span className="flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Conditional Display
                </span>
                <span>{showDependencyOptions ? '−' : '+'}</span>
              </Button>
              
              {showDependencyOptions && (
                <div className="mt-4 p-4 border rounded-lg space-y-4">
                  <div className="space-y-2">
                    <Label>Show this field when...</Label>
                    <Select
                      value={customFieldData.dependency.depends_on || "none"}
                      onValueChange={(v) => setCustomFieldData({
                        ...customFieldData,
                        dependency: { ...customFieldData.dependency, depends_on: v === "none" ? "" : v }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a field..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Always show (no dependency)</SelectItem>
                        {customFields.filter(f => f.tag !== customFieldData.tag).map(f => (
                          <SelectItem key={f.tag} value={f.tag}>{f.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {customFieldData.dependency.depends_on && (
                    <>
                      <div className="space-y-2">
                        <Label>Condition</Label>
                        <Select
                          value={customFieldData.dependency.condition}
                          onValueChange={(v) => setCustomFieldData({
                            ...customFieldData,
                            dependency: { ...customFieldData.dependency, condition: v }
                          })}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="exists">Has any value</SelectItem>
                            <SelectItem value="not_exists">Is empty</SelectItem>
                            <SelectItem value="equals">Equals</SelectItem>
                            <SelectItem value="not_equals">Does not equal</SelectItem>
                            <SelectItem value="contains">Contains</SelectItem>
                            <SelectItem value="greater_than">Greater than</SelectItem>
                            <SelectItem value="less_than">Less than</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {['equals', 'not_equals', 'contains', 'greater_than', 'less_than'].includes(customFieldData.dependency.condition) && (
                        <div className="space-y-2">
                          <Label>Value</Label>
                          <Input
                            value={customFieldData.dependency.value}
                            onChange={(e) => setCustomFieldData({
                              ...customFieldData,
                              dependency: { ...customFieldData.dependency, value: e.target.value }
                            })}
                          />
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <Switch
                          checked={customFieldData.dependency.make_required}
                          onCheckedChange={(checked) => setCustomFieldData({
                            ...customFieldData,
                            dependency: { ...customFieldData.dependency, make_required: checked }
                          })}
                        />
                        <Label>Make required when shown</Label>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomFieldDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCustomField} disabled={savingCustomField}>
              {savingCustomField && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editingCustomField ? 'Update' : 'Create'} Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
